import { Box, Tab, Tabs } from '@mui/material';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { ForbiddenState } from '../../components/PageState';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';

/** Tabs of the reference's User Settings area, each guarded by its own permission code. */
export const USER_TABS = [
  { label: 'Manage user groups', code: 'PC12', path: '/settings/users/groups' },
  { label: 'Manage master agent', code: 'PC120', path: '/settings/users/master' },
  { label: 'Manage distributor', code: 'PC121', path: '/settings/users/distributor' },
  { label: 'Manage retailer', code: 'PC122', path: '/settings/users/retailer' },
  { label: 'Manage employee', code: 'PC123', path: '/settings/users/employee' },
  { label: 'Commission matrix', code: 'PC792', path: '/settings/users/commission' },
  { label: 'Search user', code: 'PC871', path: '/settings/users/search' },
] as const;

/**
 * "Manage administrator" (switch `add-administrator`): company staff, who see the whole company.
 * This project's own tab (the reference keeps its staff elsewhere); administrators only.
 */
const ADMIN_TAB = { label: 'Manage administrator', code: 'PC123', path: '/settings/users/admin' };

export function UserSettingsLayout() {
  const { can, user } = useAuth();
  const { pathname } = useLocation();
  const featureOn = useFeatureReleased();
  const tabs = [
    ...USER_TABS,
    ...(user?.level === 'admin' && featureOn('add-administrator') ? [ADMIN_TAB] : []),
  ].filter((t) => can(t.code));

  if (tabs.length === 0) return <ForbiddenState code="PC480" />;
  if (pathname === '/settings/users' || pathname === '/settings/users/')
    return <Navigate to={tabs[0]!.path} replace />;

  const current = tabs.find((t) => pathname.startsWith(t.path))?.path ?? false;

  return (
    <Box>
      <PageHeader title="User Settings" code="PC480" />
      <Tabs
        value={current}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.path}
            value={t.path}
            label={`${t.label} (${t.code})`}
            component={Link}
            to={t.path}
          />
        ))}
      </Tabs>
      <Outlet />
    </Box>
  );
}
